"use client";

import React, { useState } from 'react';
import clsx from 'clsx';
import { PrismicNextImage } from '@prismicio/next';
import { isFilled } from '@prismicio/helpers';
import { ConditionalLink } from '@/app/lib/ConditionalLink';
import { BackgroundImage } from "@/app/lib/ParallaxBackground";
import { RichText } from '@/app/lib/RichText';
import { dashify, sliceStyle } from '@/app/lib/utils';
import { VideoPlayer } from '@/app/lib/VideoPlayer';
import { ResponsiveVimeoEmbed } from "@/app/lib/VimeoPlayer";
import { Autoplay, EffectFade, Keyboard, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './hero.module.scss';
import storyHeroStyles from '../StoryHero/storyHero.module.scss';
import sharedStyles from '@/app/shared.module.scss';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';

const getSlideGradientOpacity = (value) => {
	if (!isFilled.number(value)) return undefined;
	if (value <= 0) return 0;
	if (value >= 100) return 1;
	return value / 100;
};

const Hero = ({ slice }) => {
	const [swiper, setSwiper] = useState(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [hoverZone, setHoverZone] = useState('middle');
	const [isHoveringSlideLink, setIsHoveringSlideLink] = useState(false);

	const handleSlideClick = (index, event) => {
		if (!swiper) return;
		if (index !== activeIndex) {
			swiper.slideToLoop(index);
			return;
		}

		const rect = event.currentTarget.getBoundingClientRect();
		const clickX = event.clientX - rect.left;
		const leftThird = rect.width / 3;
		const rightThird = rect.width * (2 / 3);
		const clickZone = clickX < leftThird ? 'left' : clickX > rightThird ? 'right' : 'middle';
		if (clickZone === 'left') swiper.slidePrev();
		if (clickZone === 'right') swiper.slideNext();
	};

	const handleSlideMouseMove = (event) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const pointerX = event.clientX - rect.left;
		const leftThird = rect.width / 3;
		const rightThird = rect.width * (2 / 3);
		const newZone = pointerX < leftThird ? 'left' : pointerX > rightThird ? 'right' : 'middle';
		if (newZone !== hoverZone) setHoverZone(newZone);
	};

	if (slice?.variation === 'default') {
		return (
			<BackgroundImage slicePrimary={slice?.primary} className={clsx(
				slice?.primary?.show_on === 'Desktop only' ? styles.desktop_only : null,
				slice?.primary?.show_on === 'Mobile only' ? styles.mobile_only : null
			)}>

				<div className={clsx(styles.default)} style={sliceStyle(slice?.primary)}>

					{/* title */}
					{(isFilled.keyText(slice?.primary?.title) || isFilled.richText(slice?.primary?.content)) && (
						<div className={clsx(sharedStyles.top)}>
							<div className={clsx(sharedStyles.left)}>
								{isFilled.keyText(slice?.primary?.title) && (
									<h1 className={clsx(sharedStyles.h1)} id={dashify(slice?.primary?.title)}>{slice?.primary?.title}</h1>
								)}

								{isFilled.richText(slice?.primary?.content) && (
									<div className={sharedStyles.copy}>
										<RichText field={slice?.primary?.content} />
									</div>
								)}
							</div>
						</div>
					)}

					{isFilled.image(slice?.primary?.image) && (!slice?.primary?.video?.url) && (
						<div className={clsx(styles.main)}>
							<PrismicNextImage field={slice?.primary?.image} className={clsx(styles.image)} fallbackAlt='' />
						</div>
					)}

					{(slice?.primary?.video?.url) && (
						<div className={clsx(styles.main)}>
							<VideoPlayer
								video={slice.primary.video}
								poster={isFilled.image(slice?.primary?.image) ? slice?.primary?.image.url : null}
								className={clsx(sharedStyles.video)}
								loop={slice?.primary?.video_loop}
								autoplay={slice?.primary?.video_autoplay}
								controls={slice?.primary?.video_controls} />
						</div>
					)}

				</div>
			</BackgroundImage>
		)
	}

	if (slice?.variation === 'storyHero') {
		return (
			<BackgroundImage slicePrimary={slice?.primary} className={clsx(
				slice?.primary?.show_on === 'Desktop only' ? styles.desktop_only : null,
				slice?.primary?.show_on === 'Mobile only' ? styles.mobile_only : null
			)}>
				<div className={clsx(storyHeroStyles.story_hero)} style={sliceStyle(slice?.primary)}>
					<div className={clsx(storyHeroStyles.top)}>
						{isFilled.keyText(slice?.primary?.tag) && (
							<p className={clsx(storyHeroStyles.type)}>{slice?.primary?.tag}</p>
						)}

						{isFilled.keyText(slice?.primary?.title) && (
							<h2 className={clsx(storyHeroStyles.title)} id={dashify(slice?.primary?.title)}>{slice?.primary?.title}</h2>
						)}
					</div>

					{isFilled.image(slice?.primary?.image) && (!isFilled.linkToMedia(slice?.primary?.video)) && (
						<PrismicNextImage field={slice?.primary?.image} className={storyHeroStyles.image} fallbackAlt='' />
					)}

					{isFilled.linkToMedia(slice?.primary?.video) && (
						<VideoPlayer
							video={slice.primary.video}
							poster={isFilled.image(slice?.primary?.image) ? slice?.primary?.image.url : null}
							className={clsx(sharedStyles.video, storyHeroStyles.image)}
							autoplay={slice?.primary?.video_autoplay}
							controls={slice?.primary?.video_controls} />
					)}

					{isFilled.richText(slice?.primary?.introduction) && (
						<div className={clsx(storyHeroStyles.introduction)}>
							<RichText field={slice?.primary?.introduction} />
						</div>
					)}
				</div>
			</BackgroundImage>
		)
	}

	if (slice?.variation === 'full_width') {
		return (
			<BackgroundImage slicePrimary={slice?.primary} className={clsx('nope',
				slice?.primary?.show_on === 'Desktop only' ? styles.desktop_only : null,
				slice?.primary?.show_on === 'Mobile only' ? styles.mobile_only : null
			)}>
				<div className={clsx(styles.full_width)} style={sliceStyle(slice?.primary)}>

					{/* image */}
					<div className={clsx(styles.media_wrapper, (slice?.primary?.video_mobile_vimeo_id || slice?.primary?.video?.url) ? styles.image_fallback : styles.image_only)}>

						{/* Desktop */}
						{isFilled.image(slice?.primary?.image?.full) && slice?.primary?.flexible_height ? (
							<PrismicNextImage
								field={slice?.primary?.image?.full}
								className={clsx(styles.image, styles.image_desktop)}
								fallbackAlt=''
							/>
						) : isFilled.image(slice?.primary?.image?.full) ? (
							<PrismicNextImage
								field={slice?.primary?.image}
								className={clsx(styles.image, styles.image_desktop)}
								fallbackAlt=''
							/>
						) : null}

						{/* Mobile - with fallback */}
						{(isFilled.image(slice?.primary?.image?.mobile) && slice?.primary?.image?.mobile) ? (
							<PrismicNextImage
								field={slice?.primary?.image.mobile}
								className={clsx(styles.image, styles.image_mobile)}
								fallbackAlt=''
							/>
						) : isFilled.image(slice?.primary?.image) ? (
							<PrismicNextImage
								field={slice?.primary?.image}
								className={clsx(styles.image, styles.image_mobile)}
								fallbackAlt=''
							/>
						) : null}
					</div>

					{slice?.primary?.video_vimeo_id &&
						<ResponsiveVimeoEmbed
							videoId={slice.primary.video_vimeo_id}
							autoplay={slice?.primary?.video_autoplay}
							loop={slice?.primary?.video_loop}
							controls={slice?.primary?.video_controls} />
					}

					{(slice?.primary?.video?.url) && (
						<VideoPlayer
							video={slice.primary.video}
							poster={isFilled.image(slice?.primary?.image) ? slice?.primary?.image.url : null}
							className={clsx(sharedStyles.video)}
							loop={slice?.primary?.video_loop}
							autoplay={slice?.primary?.video_autoplay}
							controls={slice?.primary?.video_controls} />
					)}

					{/* title */}
					<div className={clsx(styles.over)}>
						{isFilled.keyText(slice?.primary?.title) && (
							<h1 className={clsx(styles.h1)} id={dashify(slice?.primary?.title)}>{slice?.primary?.title}</h1>
						)}

						{isFilled.richText(slice?.primary?.content) && (
							<div className={clsx(styles.copy)}>
								<RichText field={slice?.primary?.content} />
							</div>
						)}
					</div>

				</div>
			</BackgroundImage>
		)
	}

	if (slice?.variation === 'homepage') {
		return (
			<BackgroundImage slicePrimary={slice?.primary} className={clsx('nope',
				slice?.primary?.show_on === 'Desktop only' ? styles.desktop_only : null,
				slice?.primary?.show_on === 'Mobile only' ? styles.mobile_only : null
			)}>

				<div className={clsx(styles.homepage, slice?.primary?.video ? styles.with_video : null)} style={sliceStyle(slice?.primary)}>

					{/* image */}
					{isFilled.image(slice?.primary?.image) && (
						<div className={clsx(styles.media_wrapper, (slice?.primary?.video_mobile_vimeo_id || slice?.primary?.video?.url) ? styles.image_fallback : styles.image_only)}>

							{/* Desktop */}
							<PrismicNextImage
								field={slice?.primary?.image}
								className={clsx(styles.image, styles.image_desktop)}
								fallbackAlt=''
							/>

							{/* Mobile - with fallback */}
							{slice?.primary?.image?.mobile ? (
								<PrismicNextImage
									field={slice?.primary?.image.mobile}
									className={clsx(styles.image, styles.image_mobile)}
									fallbackAlt=''
								/>
							) : (
								<PrismicNextImage
									field={slice?.primary?.image}
									className={clsx(styles.image, styles.image_mobile)}
									fallbackAlt=''
								/>
							)}
						</div>
					)}

					{slice?.primary?.video_vimeo_id &&
						<ResponsiveVimeoEmbed
							videoId={slice.primary.video_vimeo_id}
							autoplay={slice?.primary?.video_autoplay}
							loop={slice?.primary?.video_loop}
							title={slice?.primary?.video_title}
							controls={slice?.primary?.video_controls} />
					}

					{(slice?.primary?.video?.url) && (
						<VideoPlayer
							video={slice.primary.video}
							poster={isFilled.image(slice?.primary?.image) ? slice?.primary?.image.url : null}
							className={clsx(sharedStyles.video)}
							loop={slice?.primary?.video_loop}
							autoplay={slice?.primary?.video_autoplay}
							controls={slice?.primary?.video_controls} />
					)}

					{isFilled.richText(slice?.primary?.title) && (
						<div className={clsx(styles.title)}>
							<div className={clsx(styles.top)}>
								<RichText field={slice?.primary?.title} />
							</div>

							{isFilled.richText(slice?.primary?.subtitle) && (
								<div className={clsx(styles.subtitle)}>
									<RichText field={slice?.primary?.subtitle} />
								</div>
							)}

						</div>
					)}

				</div>
			</BackgroundImage>
		)
	}

	if (slice?.variation === 'highlightsHero') {
		return (
			<BackgroundImage slicePrimary={slice?.primary} className={clsx(
				slice?.primary?.show_on === 'Desktop only' ? styles.desktop_only : null,
				slice?.primary?.show_on === 'Mobile only' ? styles.mobile_only : null
			)}>

				<div className={clsx(styles.highlights_hero)} style={sliceStyle(slice?.primary)}>

					{/* title */}
					<div className={clsx(sharedStyles.top)}>
						<div className={clsx(sharedStyles.left)}>
							{isFilled.keyText(slice?.primary?.title) && (
								<h2 className={clsx(sharedStyles.h1)} id={dashify(slice?.primary?.title)}>{slice?.primary?.title}</h2>
							)}

							{isFilled.richText(slice?.primary?.subtitle) && (
								<div className={sharedStyles.copy}>
									<RichText field={slice?.primary?.subtitle} />
								</div>
							)}
						</div>
					</div>

					<div className={clsx(styles.main)}>
						<ConditionalLink url={slice?.primary?.link} className={clsx(styles.left)}>
							<div className={clsx(styles.top)} style={{ color: slice?.primary?.top_text_color || '#fff', backgroundColor: slice?.primary?.top_background_color || '#EB008C' }}>
								{isFilled.keyText(slice?.primary?.top_title) && (
									<h2 className={clsx(styles.top_title)}>{slice?.primary?.top_title}</h2>
								)}

								{isFilled.richText(slice?.primary?.top_content) && (
									<div className={clsx(styles.top_content)}>
										<RichText field={slice?.primary?.top_content} />
									</div>
								)}
							</div>

							{isFilled.richText(slice?.primary?.bottom_content) && (
								<div className={clsx(styles.bottom)} style={{ color: slice?.primary?.bottom_text_color || '#fff', backgroundColor: slice?.primary?.bottom_background_color || '#000' }}>
									<RichText field={slice?.primary?.bottom_content} />
								</div>
							)}
						</ConditionalLink>

						{isFilled.image(slice?.primary?.image) && (
							<div className={clsx(styles.image_wrapper)}>
								<PrismicNextImage field={slice?.primary?.image} className={clsx(styles.image)} fallbackAlt='' />
							</div>
						)}

					</div>
				</div>
			</BackgroundImage>
		)
	}

	if (slice?.variation === 'carousel') {
		const slides = slice?.primary?.slide?.filter((item) => isFilled.image(item?.image) || isFilled.linkToMedia(item?.video)) || [];
		if (!slides.length) return null;

		return (
			<div className={clsx(styles.carousel,
				slice?.primary?.show_on === 'Desktop only' ? styles.desktop_only : null,
				slice?.primary?.show_on === 'Mobile only' ? styles.mobile_only : null)} style={sliceStyle(slice?.primary)}>

				<Swiper
					className={clsx('swiper', styles.swiper)}
					modules={[Autoplay, EffectFade, Keyboard, Pagination]}
					onSwiper={setSwiper}
					onActiveIndexChange={(swiperInstance) => setActiveIndex(swiperInstance.realIndex)}
					initialSlide={0}
					centeredSlides={false}
					slidesPerView={1}
					keyboard={{ enabled: true }}
					touchStartPreventDefault={false}
					threshold={10}
					{...(slice?.primary?.autoplay && {
						autoplay: {
							delay: 10000,
							pauseOnMouseEnter: false,
							disableOnInteraction: false,
						},
					})}
					loop={true}
					effect="fade"
					fadeEffect={{ crossFade: true }}
					pagination={slice?.primary?.show_pagination ? { clickable: true } : false}
				>
					{slides.map((item, index) => {
						const isCenterSlide = index === activeIndex;
						const isLeftSlide = index < activeIndex;
						const isRightSlide = index > activeIndex;
						const hasLink = (item?.link?.uid || item?.link?.url) !== undefined;
						const hasMediaVideo = Boolean(item?.video?.url);
						const centerCanGoLeft = isCenterSlide && hoverZone === 'left';
						const centerCanGoRight = isCenterSlide && hoverZone === 'right';
						const nonCenterLeft = !isCenterSlide && isLeftSlide;
						const nonCenterRight = !isCenterSlide && isRightSlide;
						const gradientOpacity = getSlideGradientOpacity(item?.gradient_opacity);

						return (
							<SwiperSlide
								style={gradientOpacity !== undefined ? { '--gradient-opacity': gradientOpacity } : undefined}
								className={clsx(styles.slide, {
									'cursor-scroll': slides.length > 1 && !isHoveringSlideLink && (centerCanGoLeft || centerCanGoRight || nonCenterLeft || nonCenterRight),
									'cursor-scroll cursor-scroll-left': slides.length > 1 && !isHoveringSlideLink && (centerCanGoLeft || nonCenterLeft),
									'cursor-scroll cursor-scroll-right': slides.length > 1 && !isHoveringSlideLink && (centerCanGoRight || nonCenterRight),
								})}
								key={index}
								onMouseMove={isCenterSlide ? handleSlideMouseMove : undefined}
								onMouseLeave={isCenterSlide ? () => setHoverZone('middle') : undefined}
								onClick={(event) => handleSlideClick(index, event)}
							>

								{isFilled.image(item?.image) && (
									<>
										<PrismicNextImage
											className={clsx(styles.image, styles.imageDesktop)}
											field={item?.image}
											fallbackAlt=''
										/>
										{isFilled.image(item?.image?.['Mobile Image']) ? (
											<PrismicNextImage
												className={clsx(styles.image, styles.imageMobile)}
												field={item?.image?.['Mobile Image']}
												fallbackAlt=''
											/>
										) : (
											<PrismicNextImage
												className={clsx(styles.image, styles.imageMobile)}
												field={item?.image}
												fallbackAlt=''
											/>
										)}
									</>
								)}

								{hasMediaVideo && (
									<div className={styles.slideMedia}>
										<VideoPlayer
											video={item.video}
											poster={isFilled.image(item?.image) ? item?.image?.url : null}
											className={clsx(sharedStyles.video)}
											autoplay
											loop
											controls={false}
										/>
									</div>
								)}

								{(isFilled.keyText(item?.tag) || isFilled.keyText(item?.title) || isFilled.richText(item?.content) || isFilled.richText(item?.extra_content) || isFilled.link(item?.link)) && (
									<div className={clsx(styles.content)}>
										{isFilled.keyText(item?.tag) && (
											<p className={clsx(styles.tag)}>{item?.tag}</p>
										)}

										{isFilled.richText(item?.title_2) && (
											<RichText field={item?.title_2} />
										)}

										{isFilled.richText(item?.content) && (
											<div className={clsx(styles.copy, styles.h2asp)}>
												<RichText field={item?.content} />
											</div>
										)}

										{isFilled.richText(item?.extra_content) && (
											<div className={clsx(styles.extraCopy, styles.h2asp)}>
												<RichText field={item?.extra_content} />
											</div>
										)}

										{hasLink && isFilled.keyText(item?.link?.text) && (
											<ConditionalLink
												url={item?.link}
												condition={isCenterSlide && hasLink}
												className={clsx(styles.linkLabel)}
												onMouseEnter={() => setIsHoveringSlideLink(true)}
												onMouseLeave={() => setIsHoveringSlideLink(false)}
												onClick={(event) => event.stopPropagation()}
											>
												{item?.link?.text}
											</ConditionalLink>
										)}
									</div>
								)}
							</SwiperSlide>
						)
					})}
				</Swiper>
			</div >
		)
	}

};

export default Hero;